import type {
  AddBookResponse,
  ApiErrorBody,
  Book,
  BookListResponse,
  BookSize,
  BookResponse,
  Bottle,
  BottleDetails,
  BottleListResponse,
  BottleResponse,
  Category,
  DrinkLookupResponse,
  FriendBarResponse,
  CategoryListResponse,
  CategoryResponse,
  Friend,
  FriendCollectionResponse,
  FriendRequestOutcome,
  FriendRequestResponse,
  FriendResponse,
  FriendsResponse,
  HouseholdInviteOutcome,
  HouseholdInviteResponse,
  HouseholdResponse,
  LookupResponse,
  MeResponse,
  User,
} from '../../shared/api';

/** An API failure. `status` is 0 when no HTTP response was received (timeout or network error). */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  timeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 10_000;

const unauthenticatedListeners = new Set<() => void>();

/** Registers a callback for when any request finds the session has ended. Returns an unsubscribe function. */
export function onUnauthenticated(listener: () => void): () => void {
  unauthenticatedListeners.add(listener);
  return () => unauthenticatedListeners.delete(listener);
}
// Longer than the server's worst case (Open Library, then Google Books, 8s each), so the
// server's own timeout error explains what happened.
const LOOKUP_TIMEOUT_MS = 25_000;

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, timeoutMs = DEFAULT_TIMEOUT_MS } = options;
  const controller = new AbortController();
  let timedOut = false;
  const timer = window.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  const forwardAbort = () => controller.abort();
  if (signal?.aborted) controller.abort();
  signal?.addEventListener('abort', forwardAbort, { once: true });

  const failure = (error: unknown): unknown => {
    if (timedOut) return new ApiError(0, 'TIMEOUT', 'The server took too long to respond. Please try again.');
    if (signal?.aborted) return error; // cancelled by the caller: rethrow the AbortError untouched
    return new ApiError(0, 'NETWORK_ERROR', 'Could not reach the server. Check your connection and try again.');
  };

  try {
    let response: Response;
    try {
      response = await fetch(path, {
        method,
        signal: controller.signal,
        headers: body === undefined ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch (error) {
      throw failure(error);
    }

    if (response.status === 204) return undefined as T;

    let payload: unknown;
    try {
      payload = await response.json();
    } catch (error) {
      if (timedOut || signal?.aborted) throw failure(error);
      payload = undefined;
    }

    if (!response.ok) {
      if (response.status === 401) for (const listener of unauthenticatedListeners) listener();
      const apiError = isApiErrorBody(payload) ? payload.error : null;
      throw new ApiError(
        response.status,
        apiError?.code ?? 'HTTP_ERROR',
        apiError?.message ?? `The server returned an error (HTTP ${response.status}).`,
      );
    }
    if (payload === undefined) {
      throw new ApiError(response.status, 'INVALID_RESPONSE', 'The server sent an unexpected response.');
    }
    return payload as T;
  } finally {
    window.clearTimeout(timer);
    signal?.removeEventListener('abort', forwardAbort);
  }
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== 'object' || value === null || !('error' in value)) return false;
  const error = (value as { error: unknown }).error;
  return typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string';
}

export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

export function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return 'Something went wrong. Please try again.';
}

const bookPath = (isbn: string) => `/api/books/${encodeURIComponent(isbn)}`;

export const api = {
  me: async (signal?: AbortSignal): Promise<User> => (await request<MeResponse>('/api/auth/me', { signal })).user,

  logout: () => request<void>('/api/auth/logout', { method: 'POST' }),

  lookup: (isbn: string, signal?: AbortSignal) =>
    request<LookupResponse>(`/api/lookup/${encodeURIComponent(isbn)}`, { signal, timeoutMs: LOOKUP_TIMEOUT_MS }),

  addBook: (isbn: string, categoryId: number | null = null, size: BookSize | null = null) =>
    request<AddBookResponse>('/api/books', { method: 'POST', body: { isbn, categoryId, size } }),

  listBooks: async (signal?: AbortSignal): Promise<Book[]> =>
    (await request<BookListResponse>('/api/books', { signal })).books,

  getBook: async (isbn: string, signal?: AbortSignal): Promise<Book> =>
    (await request<BookResponse>(bookPath(isbn), { signal })).book,

  deleteBook: (isbn: string) => request<void>(bookPath(isbn), { method: 'DELETE' }),

  refreshBook: async (isbn: string): Promise<Book> =>
    (await request<BookResponse>(`${bookPath(isbn)}/refresh`, { method: 'POST' })).book,

  setBookCategories: async (isbn: string, categoryIds: number[]): Promise<Book> =>
    (await request<BookResponse>(`${bookPath(isbn)}/categories`, { method: 'PUT', body: { categoryIds } })).book,

  setBookSize: async (isbn: string, size: BookSize | null): Promise<Book> =>
    (await request<BookResponse>(`${bookPath(isbn)}/size`, { method: 'PUT', body: { size } })).book,

  listCategories: async (signal?: AbortSignal): Promise<Category[]> =>
    (await request<CategoryListResponse>('/api/categories', { signal })).categories,

  createCategory: async (name: string): Promise<Category> =>
    (await request<CategoryResponse>('/api/categories', { method: 'POST', body: { name } })).category,

  renameCategory: async (id: number, name: string): Promise<Category> =>
    (await request<CategoryResponse>(`/api/categories/${id}`, { method: 'PATCH', body: { name } })).category,

  deleteCategory: (id: number) => request<void>(`/api/categories/${id}`, { method: 'DELETE' }),

  listFriends: (signal?: AbortSignal) => request<FriendsResponse>('/api/friends', { signal }),

  sendFriendRequest: async (email: string): Promise<FriendRequestOutcome> =>
    (await request<FriendRequestResponse>('/api/friends/requests', { method: 'POST', body: { email } })).outcome,

  acceptFriendRequest: async (id: number): Promise<Friend> =>
    (await request<FriendResponse>(`/api/friends/requests/${id}/accept`, { method: 'POST' })).friend,

  /** Cancels a request you sent, or declines one sent to you. */
  deleteFriendRequest: (id: number) => request<void>(`/api/friends/requests/${id}`, { method: 'DELETE' }),

  removeFriend: (id: number) => request<void>(`/api/friends/${id}`, { method: 'DELETE' }),

  friendCollection: (id: number, signal?: AbortSignal) =>
    request<FriendCollectionResponse>(`/api/friends/${id}/books`, { signal }),

  household: (signal?: AbortSignal) => request<HouseholdResponse>('/api/household', { signal }),

  inviteToHousehold: async (email: string): Promise<HouseholdInviteOutcome> =>
    (await request<HouseholdInviteResponse>('/api/household/invites', { method: 'POST', body: { email } })).outcome,

  acceptHouseholdInvite: (id: number) => request<HouseholdResponse>(`/api/household/invites/${id}/accept`, { method: 'POST' }),

  /** Cancels an invitation you sent, or declines one sent to you. */
  deleteHouseholdInvite: (id: number) => request<void>(`/api/household/invites/${id}`, { method: 'DELETE' }),

  leaveHousehold: () => request<void>('/api/household/leave', { method: 'POST' }),

  friendBar: (id: number, signal?: AbortSignal) => request<FriendBarResponse>(`/api/friends/${id}/bottles`, { signal }),

  lookupDrink: (barcode: string, signal?: AbortSignal) =>
    request<DrinkLookupResponse>(`/api/drinks/lookup/${encodeURIComponent(barcode)}`, { signal, timeoutMs: LOOKUP_TIMEOUT_MS }),

  listBottles: async (signal?: AbortSignal): Promise<Bottle[]> =>
    (await request<BottleListResponse>('/api/bottles', { signal })).bottles,

  getBottle: async (id: number, signal?: AbortSignal): Promise<Bottle> =>
    (await request<BottleResponse>(`/api/bottles/${id}`, { signal })).bottle,

  addBottle: async (details: BottleDetails): Promise<Bottle> =>
    (await request<BottleResponse>('/api/bottles', { method: 'POST', body: details })).bottle,

  updateBottle: async (id: number, changes: Partial<BottleDetails>): Promise<Bottle> =>
    (await request<BottleResponse>(`/api/bottles/${id}`, { method: 'PATCH', body: changes })).bottle,

  pourBottle: async (id: number, count: number): Promise<Bottle> =>
    (await request<BottleResponse>(`/api/bottles/${id}/pours`, { method: 'POST', body: { count } })).bottle,

  /** A real check of the bottle: sets the actual level (and optionally how many are on hand). */
  setBottleLevel: async (id: number, level: number, quantity?: number): Promise<Bottle> =>
    (await request<BottleResponse>(`/api/bottles/${id}/level`, { method: 'PUT', body: { level, quantity } })).bottle,

  deleteBottle: (id: number) => request<void>(`/api/bottles/${id}`, { method: 'DELETE' }),

  /** Uploads a photo (already shrunk, see lib/photo.ts) as the bottle's picture. */
  setBottlePhoto: async (id: number, photo: Blob): Promise<Bottle> => {
    const response = await fetch(`/api/bottles/${id}/photo`, {
      method: 'PUT',
      headers: { Accept: 'application/json', 'Content-Type': photo.type || 'application/octet-stream' },
      body: photo,
    });
    const payload: unknown = await response.json().catch(() => undefined);
    if (!response.ok) {
      const error = isApiErrorBody(payload) ? payload.error : null;
      throw new ApiError(response.status, error?.code ?? 'HTTP_ERROR', error?.message ?? `The server returned an error (HTTP ${response.status}).`);
    }
    return (payload as BottleResponse).bottle;
  },

  deleteBottlePhoto: async (id: number): Promise<Bottle> =>
    (await request<BottleResponse>(`/api/bottles/${id}/photo`, { method: 'DELETE' })).bottle,
};

/** Full-page navigation to Google; the server sends the browser back to `returnTo` afterwards. */
export function signInUrl(returnTo: string): string {
  return `/api/auth/google?returnTo=${encodeURIComponent(returnTo)}`;
}

export const EXPORT_URLS = {
  json: '/api/export/json',
  csv: '/api/export/csv',
  barJson: '/api/export/bar.json',
  barCsv: '/api/export/bar.csv',
} as const;
